import React, {
    ChangeEvent,
    DetailedHTMLProps,
    HTMLAttributes,
    InputHTMLAttributes,
    RefAttributes,
    useRef,
    useState
} from "react";
import {IonButton, IonIcon, IonImg, IonToggle} from "@ionic/react";
import {camera, cloudUpload, trash, repeat} from "ionicons/icons";
import {IFileUploadSettings, FormDataResponse, FormDataVO} from "../../../../ts/manager/FileManager";
import {getManager} from "../../../../ts/manager/Manager";
import ErrorWrapperFE from "../form-element/ErrorWrapperFE";
import {
    FileFormItem,
    IFormItemElement, IImageFileFormItemProps,
} from "../model/FormModel";
import {StyleReactProps} from "@ionic/react/dist/types/components/react-component-lib/interfaces";
import {JSX} from "react/jsx-runtime";
import IntrinsicAttributes = JSX.IntrinsicAttributes;
import ImageVO from "../../../../ts/common/ImageVO";
import {normalizeFileAccept} from "../../../../ts/common/SiteHelper";
import {ImageFileReplaceVO} from "../model/vo/FileReplaceVO";

interface IProps extends IFormItemElement
{
    formItemProps:IImageFileFormItemProps;
    requiredMessage?: string;
    hideCamera?: boolean;
    fileUploadSettings?:IFileUploadSettings
    toggleElementProps?: IntrinsicAttributes & React.ComponentProps<typeof IonToggle> & Omit<HTMLAttributes<HTMLIonToggleElement>, "style"> & StyleReactProps & RefAttributes<typeof IonToggle>
    fileElementProps?: DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
    imageElementProps?: IntrinsicAttributes & React.ComponentProps<typeof IonImg> & Omit<HTMLAttributes<HTMLIonImgElement>, "style"> & StyleReactProps & RefAttributes<typeof IonImg>
}

const ImageInputFIE = (props: IProps) =>
{
    let {
        form,
        onItemChange,
        fileElementProps,
        formItemProps,
        label,
        instructions,
        imageElementProps
    } = props;

    const formItem = form.requireFormItem(formItemProps, "file", true) as FileFormItem;
    if(formItem.value.fileType !== "image"){
        formItem.props.value = new ImageFileReplaceVO();
        formItem.update(formItem.props)
    }
    const savedFileVOs:ImageVO[] = formItem.value.savedFileVOs as ImageVO[];

    label = label ?? formItem.name;
    label = label || !formItem.required ? label : " ";

    const [loading, setLoading] = useState<boolean>(false);
    const inputElementRef = useRef<HTMLInputElement|null>(null);

    const fileUploadSettings:IFileUploadSettings = {
        mimeTypes: normalizeFileAccept(props.fileUploadSettings?.mimeTypes ?? "image/jpg, image/jpeg, image/png, image/gif, image/bmp"),
        fileSizeMax: props.fileUploadSettings?.fileSizeMax ?? 10 * 1024 * 1024,
        fileLimit: props.fileUploadSettings?.fileLimit ?? 1,
        itemTypeName: props.fileUploadSettings?.itemTypeName ?? "image",
        itemTypeNamePlural: props.fileUploadSettings?.itemTypeNamePlural ?? "images"
    }
    fileUploadSettings.errorFileLimit = fileUploadSettings.fileLimit;
    const fileLimit = fileUploadSettings.fileLimit ?? 0;
    if((fileLimit > 0) && (savedFileVOs.length > fileLimit)){
        savedFileVOs.length = fileUploadSettings.fileLimit!
    }

    const inputElement: (() => HTMLInputElement) = () =>
    {
        return inputElementRef?.current as HTMLInputElement;
    }

    const currentImageSources = ():(ImageVO|FormDataVO)[] =>
    {
        const sources:(ImageVO|FormDataVO)[] = [];
        for(let fileVO of savedFileVOs){
            if(!formItem.value.deleteFileIDs.includes(fileVO.id)){
                sources.push(fileVO);
            }
        }
        return sources.concat(formItem.value.formDataVOs);
    }



    const remainingFileLimit = () => {
        const settingsLimit = fileUploadSettings.fileLimit ?? -1;
        if(settingsLimit === 1){
            return 1;
        }
        if(settingsLimit < 1){
            return -1;
        }
        const remaining = settingsLimit - currentImageSources().length
        if(remaining <= 0){
            return 0;
        }
        return remaining;
    }

    const initSubmitState = () =>
    {
        setLoading(true);
        form.refreshState();
    }

    const handleOnChange = () => {
        if(onItemChange){
            onItemChange(formItem.itemID);
            form.refreshState();
        } else if(onItemChange !== null){
            form.onFormItemChange(formItem.itemID);
        }
    }

    const handleFormDataResponse = (response: FormDataResponse) =>
    {
        formItem.errorText = response.errors[0] ?? "";
        const didSubmit = response.valid;
        setLoading(false);
        if(didSubmit){
            const replaceVO = formItem.value.clone();
            if(fileUploadSettings.fileLimit === 1){
                replaceVO.formDataVOs = [];
                replaceVO.deleteFileIDs = [];
                for(let fileVO of replaceVO.savedFileVOs){
                    replaceVO.deleteFileIDs.push(fileVO.id);
                }
            }
            replaceVO.formDataVOs = replaceVO.formDataVOs.concat(response.formDataVOs)
            formItem.setValue(replaceVO);
            handleOnChange();
        } else if(formItem.errorText){
            if (response.webPath)
            {
                URL.revokeObjectURL(response.webPath);
            }
        }
        form.refreshState();
    }

    const uploadFiles = (files: FileList) =>
    {
        initSubmitState();

        const settings:IFileUploadSettings = {
            ...fileUploadSettings,
            fileLimit: remainingFileLimit()
        }
        const response = getManager().file.getFileInputFormData(files, settings)
        handleFormDataResponse(response)
    }

    const onSelectFile = (event:ChangeEvent) =>
    {
        const inputElement = event.target as HTMLInputElement;
        uploadFiles((inputElement.files as FileList) ?? [])
        inputElement.value = "";
    }

    const cameraCapture = () =>
    {
        initSubmitState();

        getManager().file.getCameraCaptureFormData().then((response: FormDataResponse) => handleFormDataResponse(response))
    }

    const removeFile = (file:ImageVO|FormDataVO) =>
    {
        formItem.errorText = "";

        const replaceVO = formItem.value.clone();

        if(file instanceof ImageVO){
            if(!replaceVO.deleteFileIDs.includes(file.id)){
                replaceVO.deleteFileIDs.push(file.id);
            }
        } else {
            for(let i = 0; i < replaceVO.formDataVOs.length; i++){
                if(replaceVO.formDataVOs[i] === file){
                    replaceVO.formDataVOs.splice(i, 1);
                    i --;
                }
            }
            if (file.webPath)
            {
                URL.revokeObjectURL(file.webPath);
            }
        }
        formItem.setValue(replaceVO)

        handleOnChange();
        form.refreshState();
    }


    const applyResetFormData = () =>
    {
        const replaceVO = formItem.value.clone();
        replaceVO.formDataVOs = [];
        replaceVO.deleteFileIDs = [];

        formItem.setValue(replaceVO)
        handleOnChange();
        form.refreshState();
    }

    const canReset = () => {
        if(formItem.value.formDataVOs.length){
            return true;
        }
        for(let fileVO of savedFileVOs){
            if(formItem.value.deleteFileIDs.includes(fileVO.id)){
                return true;
            }

        }
        return false;
    }
    return <div className={form.isFormItemHidden(formItem) ? "d-none" : ""}>
        <ErrorWrapperFE
            formItem={formItem}
            elementProps={{
                className: loading ? "content-disabled" : ""
            }}
        >

            <fieldset
                data-form-item-id={formItem.itemID}
            >
                <div className="row">
                    {!!label &&
                        <div className="col-12">
                            <label
                                className={"form-item-label" + (formItem.required ? " required" : "")}
                            >
                                {label}
                                <input id={formItem.itemID} className="d-none" />
                            </label>
                        </div>
                    }
                    {!!instructions && (
                        <div className="col-12">
                            <span className="instructions">{instructions}</span>
                        </div>
                    )}
                    <div className="col-12 text-start">
                        <input
                            {...fileElementProps}
                            type="file"
                            data-form-item-id={formItem.itemID}
                            hidden
                            ref={inputElementRef}
                            accept={fileUploadSettings.mimeTypes}
                            onChange={onSelectFile}
                            multiple={remainingFileLimit() !== 1}
                        />

                        <IonButton
                            className="btn primary reduced icon-only ms-1"
                            color="primary"
                            onClick={() =>
                            {
                                inputElement().click();
                            }}
                            disabled={!remainingFileLimit()}
                        >
                            <IonIcon
                                icon={cloudUpload}
                            />
                        </IonButton>

                        {!props.hideCamera && <>
                            <IonButton
                                className="btn primary reduced icon-only ms-1"
                                color="primary"
                                onClick={cameraCapture}
                                disabled={!remainingFileLimit()}
                            >
                                <IonIcon
                                    icon={camera}
                                />
                            </IonButton>
                        </>}
                        <IonButton
                            className="btn primary reduced icon-only ms-1"
                            color="primary"
                            onClick={applyResetFormData}
                            disabled={!canReset()}
                        >
                            <IonIcon
                                icon={repeat}
                            />
                        </IonButton>
                    </div>

                    {currentImageSources().map((imageSource, index) =>
                    {
                        const src = imageSource instanceof ImageVO ? imageSource.size.thumbnail.url : imageSource.webPath;
                        return <div
                            className="col-12 col-md-6 col-lg-4 mb-4"
                            key={index}
                        >
                            <div className="row">

                                <div className="col-12 text-end">

                                    <IonButton
                                        className="btn secondary icon-only reduced ms-1"
                                        color="primary"
                                        onClick={() => removeFile(imageSource)}
                                    >
                                        <IonIcon
                                            icon={trash}
                                        />
                                    </IonButton>
                                </div>

                                <div className="col-12">

                                    <IonImg
                                        {...imageElementProps}
                                        className="image-preview"
                                        src={src}
                                        alt={formItem.name}
                                    />
                                </div>
                            </div>
                        </div>
                    })}
                </div>
            </fieldset>
        </ErrorWrapperFE>
    </div>;
}

export default ImageInputFIE;